import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { Behavior } from '../types';

export interface SceneObject {
  uuid: string;
  name: string;
  type: 'cube' | 'sphere' | 'plane' | 'model';
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number };
  scale: { x: number; y: number; z: number };
  color: string;
  src?: string;
  fitHeight?: number;
  parentId?: string;
  behaviors?: Behavior[];
}

export class Scene3D {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  controls: OrbitControls;
  transformControls: any;
  objects: Map<string, THREE.Object3D> = new Map();
  selectedObject: THREE.Object3D | null = null;
  gridHelper: THREE.GridHelper;
  axesHelper: THREE.AxesHelper;
  // Bridge so the React store can mirror gizmo-driven transform changes.
  onObjectChange: ((uuid: string, data: SceneObject) => void) | null = null;
  // Per-frame hook (used by Play mode to run a game loop); null in the editor.
  onFrame: ((dt: number) => void) | null = null;
  private clock = new THREE.Clock();
  private gltfLoader = new GLTFLoader();
  private mixers: THREE.AnimationMixer[] = [];

  constructor(canvas: HTMLCanvasElement) {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x1a1a1a);

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    this.camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 10000);
    this.camera.position.set(5, 5, 5);
    this.camera.lookAt(0, 0, 0);

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.shadowMap.enabled = true;

    // Orbit / zoom / pan camera controls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.screenSpacePanning = true;
    this.controls.minDistance = 2;
    this.controls.maxDistance = 200;
    this.controls.target.set(0, 0, 0);
    this.controls.update();

    // Move / rotate / scale gizmo
    this.transformControls = new TransformControls(this.camera, this.renderer.domElement);
    // Disable orbiting while dragging a gizmo handle
    this.transformControls.addEventListener('dragging-changed', (event: any) => {
      this.controls.enabled = !event.value;
    });
    // Mirror live transform changes back to the store
    this.transformControls.addEventListener('objectChange', () => {
      if (this.selectedObject && this.onObjectChange) {
        const data = this.getObjectData(this.selectedObject.uuid);
        if (data) this.onObjectChange(this.selectedObject.uuid, data);
      }
    });
    this.scene.add(this.transformControls);

    this.setupLighting();

    this.gridHelper = new THREE.GridHelper(20, 20, 0x444444, 0x222222);
    this.scene.add(this.gridHelper);

    this.axesHelper = new THREE.AxesHelper(5);
    this.scene.add(this.axesHelper);

    window.addEventListener('resize', () => this.onWindowResize(canvas));
  }

  setTransformMode(mode: 'translate' | 'rotate' | 'scale') {
    if (this.transformControls) this.transformControls.setMode(mode);
  }

  private setupLighting() {
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(10, 10, 10);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    this.scene.add(directionalLight);
  }

  private createGeometry(type: SceneObject['type']): THREE.BufferGeometry {
    switch (type) {
      case 'cube':
        return new THREE.BoxGeometry(1, 1, 1);
      case 'sphere':
        return new THREE.SphereGeometry(1, 32, 32);
      case 'plane':
        return new THREE.PlaneGeometry(1, 1);
      default:
        return new THREE.BoxGeometry(1, 1, 1);
    }
  }

  addObject(type: 'cube' | 'sphere' | 'plane'): SceneObject {
    const geometry = this.createGeometry(type);

    const material = new THREE.MeshStandardMaterial({
      color: Math.random() * 0xffffff,
      roughness: 0.7,
      metalness: 0.2,
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    const index = this.objects.size;
    const perRow = 5;
    const spacing = 2;
    const x = (index % perRow) * spacing - ((perRow - 1) * spacing) / 2;
    const z = Math.floor(index / perRow) * spacing;
    const y = type === 'plane' ? 0 : 0.5;
    mesh.position.set(x, y, z);

    const name = `${type.charAt(0).toUpperCase() + type.slice(1)}_${this.objects.size + 1}`;
    mesh.userData.name = name;
    mesh.userData.type = type;
    mesh.userData.behaviors = [];
    mesh.userData.parentId = undefined;

    this.scene.add(mesh);
    this.objects.set(mesh.uuid, mesh);

    const sceneObject: SceneObject = {
      uuid: mesh.uuid,
      name,
      type,
      position: { x, y, z },
      rotation: { x: 0, y: 0, z: 0 },
      scale: { x: 1, y: 1, z: 1 },
      color: '#' + material.color.getHexString(),
      behaviors: [],
    };

    return sceneObject;
  }

  // Build a scene node from a stored SceneObject. Primitives become a Mesh;
  // models become a Group whose .glb geometry streams in asynchronously.
  // Transforms are applied as given; for a child they are LOCAL to its parent.
  private createObject(obj: SceneObject): THREE.Object3D {
    let node: THREE.Object3D;

    if (obj.type === 'model') {
      const group = new THREE.Group();
      node = group;
      if (obj.src) {
        this.gltfLoader.load(
          obj.src,
          (gltf) => {
            gltf.scene.traverse((child: THREE.Object3D) => {
              if ((child as THREE.Mesh).isMesh) {
                child.castShadow = true;
                child.receiveShadow = true;
              }
            });
            // Auto-fit: scale the model so its height matches fitHeight (in grid
            // units), regardless of the file's native size. The group's own
            // scale then multiplies on top, so fitHeight=1.8 + scale=1 → 1.8 tall.
            if (obj.fitHeight && obj.fitHeight > 0) {
              gltf.scene.updateMatrixWorld(true);
              const box = new THREE.Box3().setFromObject(gltf.scene);
              const size = new THREE.Vector3();
              box.getSize(size);
              if (size.y > 1e-4) {
                gltf.scene.scale.multiplyScalar(obj.fitHeight / size.y);
              }
            }
            group.add(gltf.scene);
            // If the model ships with animations, loop its idle/default clip.
            if (gltf.animations && gltf.animations.length) {
              const mixer = new THREE.AnimationMixer(gltf.scene);
              const idle =
                gltf.animations.find((a) => /idle/i.test(a.name)) ??
                gltf.animations[0];
              mixer.clipAction(idle).play();
              this.mixers.push(mixer);
              group.userData.mixer = mixer;
            }
          },
          undefined,
          (err) => {
            console.error('Failed to load model', obj.src, err);
          }
        );
      }
    } else {
      const geometry = this.createGeometry(obj.type);
      const material = new THREE.MeshStandardMaterial({
        color: new THREE.Color(obj.color || '#ffffff'),
        roughness: 0.7,
        metalness: 0.2,
      });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      node = mesh;
    }

    node.uuid = obj.uuid;
    node.position.set(obj.position.x, obj.position.y, obj.position.z);
    node.rotation.set(obj.rotation.x, obj.rotation.y, obj.rotation.z);
    node.scale.set(obj.scale.x, obj.scale.y, obj.scale.z);
    node.userData.name = obj.name;
    node.userData.type = obj.type;
    node.userData.src = obj.src;
    node.userData.fitHeight = obj.fitHeight;
    node.userData.behaviors = obj.behaviors ?? [];
    node.userData.parentId = obj.parentId;
    return node;
  }

  // Re-parent registered nodes according to their stored parentId.
  // Stored transforms are treated as local, so we use .add() (not .attach()).
  private applyParenting(objects: SceneObject[]) {
    objects.forEach((obj) => {
      if (!obj.parentId) return;
      const child = this.objects.get(obj.uuid);
      const parent = this.objects.get(obj.parentId);
      if (child && parent) parent.add(child);
    });
  }

  loadObjects(objects: SceneObject[]) {
    this.clearScene();
    // Pass 1: create every node and register it (added flat to the scene).
    objects.forEach((obj) => {
      const node = this.createObject(obj);
      this.scene.add(node);
      this.objects.set(node.uuid, node);
    });
    // Pass 2: nest children under their parents (order-independent).
    this.applyParenting(objects);
  }

  // Add a pre-grouped set of objects (a composite figure, or a single model)
  // to the live scene. specs[0] is treated as the root.
  addComposite(specs: SceneObject[]) {
    specs.forEach((obj) => {
      const node = this.createObject(obj);
      this.scene.add(node);
      this.objects.set(node.uuid, node);
    });
    this.applyParenting(specs);
  }

  removeObject(uuid: string) {
    const obj = this.objects.get(uuid);
    if (!obj) return;

    // Cascade: remove any children parented to this object first.
    const childIds: string[] = [];
    this.objects.forEach((o) => {
      if ((o as any).userData.parentId === uuid) childIds.push(o.uuid);
    });
    childIds.forEach((cid) => this.removeObject(cid));

    if (this.selectedObject?.uuid === uuid) {
      this.transformControls.detach();
      this.selectedObject = null;
    }
    // Drop any animation mixer bound to this object.
    const mixer = (obj as any).userData.mixer as THREE.AnimationMixer | undefined;
    if (mixer) {
      mixer.stopAllAction();
      this.mixers = this.mixers.filter((m) => m !== mixer);
    }
    // A parented child's parent is its group, not the scene.
    if (obj.parent) obj.parent.remove(obj);
    else this.scene.remove(obj);
    this.objects.delete(uuid);
  }

  selectObject(uuid: string) {
    if (this.selectedObject) {
      const edges = this.selectedObject.children.find(
        (child: THREE.Object3D) => child instanceof THREE.LineSegments
      ) as THREE.LineSegments | undefined;
      if (edges) {
        this.selectedObject.remove(edges);
        edges.geometry.dispose();
        (edges.material as THREE.Material).dispose();
      }
    }

    const obj = this.objects.get(uuid);
    if (obj) {
      this.selectedObject = obj;
      const mesh = obj as THREE.Mesh;
      if (mesh.isMesh && mesh.geometry) {
        const edges = new THREE.EdgesGeometry(mesh.geometry);
        const line = new THREE.LineSegments(
          edges,
          new THREE.LineBasicMaterial({ color: 0x00ff00 })
        );
        obj.add(line);
      }
      this.transformControls.attach(obj);
    }
  }

  setColor(uuid: string, color: string) {
    const obj = this.objects.get(uuid) as THREE.Mesh | undefined;
    if (!obj) return;
    const material = obj.material as THREE.MeshStandardMaterial;
    if (material && material.color) {
      material.color.set(color);
    }
  }

  updateObject(uuid: string, updates: Partial<SceneObject>) {
    const obj = this.objects.get(uuid);
    if (!obj) return;

    if (updates.position) {
      obj.position.set(updates.position.x, updates.position.y, updates.position.z);
    }
    if (updates.rotation) {
      obj.rotation.set(updates.rotation.x, updates.rotation.y, updates.rotation.z);
    }
    if (updates.scale) {
      obj.scale.set(updates.scale.x, updates.scale.y, updates.scale.z);
    }
    if (updates.color) {
      this.setColor(uuid, updates.color);
    }
    if (updates.name !== undefined) {
      (obj as any).userData.name = updates.name;
    }
    if (updates.behaviors !== undefined) {
      (obj as any).userData.behaviors = updates.behaviors;
    }
  }

  getObjectData(uuid: string): SceneObject | null {
    const obj = this.objects.get(uuid);
    if (!obj) return null;

    return {
      uuid,
      name: (obj as any).userData.name || 'Object',
      type: (obj as any).userData.type || 'cube',
      position: { x: obj.position.x, y: obj.position.y, z: obj.position.z },
      rotation: { x: obj.rotation.x, y: obj.rotation.y, z: obj.rotation.z },
      scale: { x: obj.scale.x, y: obj.scale.y, z: obj.scale.z },
      color: '#' + ((obj as any).material?.color?.getHexString() || '000000'),
      src: (obj as any).userData.src,
      fitHeight: (obj as any).userData.fitHeight,
      parentId: (obj as any).userData.parentId,
      behaviors: ((obj as any).userData.behaviors ?? []) as Behavior[],
    };
  }

  getAllObjects(): SceneObject[] {
    const result: SceneObject[] = [];
    this.objects.forEach((obj) => {
      const data = this.getObjectData(obj.uuid);
      if (data) result.push(data);
    });
    return result;
  }

  clearScene() {
    this.transformControls.detach();
    this.mixers = [];
    this.objects.forEach((obj) => {
      this.scene.remove(obj);
    });
    this.objects.clear();
    this.selectedObject = null;
  }

  animate() {
    requestAnimationFrame(() => this.animate());
    const dt = this.clock.getDelta();
    for (const mixer of this.mixers) mixer.update(dt);
    if (this.onFrame) this.onFrame(dt);
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }

  private onWindowResize(canvas: HTMLCanvasElement) {
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  dispose() {
    this.transformControls.dispose();
    this.controls.dispose();
    this.renderer.dispose();
    window.removeEventListener('resize', () => this.onWindowResize);
  }
}
